import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  getDoc
} from 'firebase/firestore';
import { createIncidentLog, ACTIVITY_TYPES, getUserFullName } from '../utils/activityLogger';
import MultiLayerIntelligence from './multiLayerIntelligence.js';

/**
 * Media Intelligence Service - Core Platform
 * 
 * Extensible media monitoring system for crisis management.
 * Supports multiple feed providers (RSS, APIs, social media).
 * 
 * Phase 1: Google News RSS with Enhanced Filtering
 * Phase 2: NewsAPI, Twitter, Social Media
 * Phase 3: Premium feeds and custom scraping
 */
class MediaIntelligenceService {
  constructor(db, options = {}) {
    this.db = db;
    this.feedProviders = [];
    this.isRunning = true;
    this.refreshInterval = null;
    this.articleProcessors = [];
    
    // Enhanced configuration options
    this.config = {
      // Minimum relevance score to store articles (0-1)
      minRelevanceScore: options.minRelevanceScore || 0.3,
      
      // Filter sensitivity ('strict', 'medium', 'relaxed')
      filterSensitivity: options.filterSensitivity || 'medium',
      
      // Store filtered articles for analysis (debugging)
      storeFilteredArticles: options.storeFilteredArticles || false,
      
      // Maximum articles to process per cycle
      maxArticlesPerCycle: options.maxArticlesPerCycle || 500, // INCREASED FOR DEBUGGING
      
      // Enable advanced filtering
      enableAdvancedFiltering: options.enableAdvancedFiltering !== false,
      
      // 🧠 GPT SCORING FEATURE FLAG (PHASE 2 SAFETY)
      // When true: Uses GPT for relevance scoring with smart content truncation
      // When false: Uses existing keyword-based scoring system (DEFAULT)
      useGPTScoring: options.useGPTScoring === true, // Explicit true required, defaults to false
      
      // 🧠 MULTI-LAYER INTELLIGENCE ENHANCEMENT (PHASES 1 & 2)
      // When true: Enhances keyword scoring with 7 intelligence layers
      // When false: Uses basic keyword scoring only (for debugging)
      useMultiLayerIntelligence: options.useMultiLayerIntelligence !== false, // Enabled by default
      
      ...options
    };
    
    // Performance tracking
    this.metrics = {
      articlesProcessed: 0,
      articlesFiltered: 0,
      alertsGenerated: 0,
      errorCount: 0,
      lastRunTime: Date.now(),
      processingTime: 0,
      relevanceScores: []
    };
    
    console.log('🔍 Enhanced Media Intelligence Service initialized', {
      minRelevanceScore: this.config.minRelevanceScore,
      filterSensitivity: this.config.filterSensitivity,
      enableAdvancedFiltering: this.config.enableAdvancedFiltering,
      useGPTScoring: this.config.useGPTScoring,
      scoringMode: this.config.useGPTScoring ? '🧠 GPT + Smart Truncation' : '🔤 Keyword-based (Default)',
      isRunning: this.isRunning
    });
    
    // 🛡️ FEATURE FLAG STATUS
    if (this.config.useGPTScoring) {
      console.log('🧠 GPT SCORING ENABLED: Using AI-powered relevance assessment with smart content truncation');
      this.initializeGPTComponents();
    } else {
      console.log('🔤 KEYWORD SCORING ACTIVE: Using traditional keyword-based relevance assessment (default mode)');
    }
    
    // 🧠 MULTI-LAYER INTELLIGENCE INITIALIZATION
    this.initializeMultiLayerIntelligence();
  }

  /**
   * 🧠 INITIALIZE MULTI-LAYER INTELLIGENCE (PHASES 1 & 2)
   * 
   * Initializes the multi-layer intelligence enhancement system.
   * This works alongside existing keyword scoring to dramatically improve accuracy.
   */
  initializeMultiLayerIntelligence() {
    try {
      if (this.config.useMultiLayerIntelligence) {
        console.log('🧠 INITIALIZING MULTI-LAYER INTELLIGENCE...');
        
        this.multiLayerIntelligence = new MultiLayerIntelligence({
          enableContextValidation: true,
          enableGeographicIntelligence: true,
          enableSourceCredibility: true,
          enableNegativeFiltering: true,
          enableEmergencyAuthorities: true,
          enableAgeDecay: true,
          enableEntityDisambiguation: true
        });
        
        // Add intelligence metrics to system metrics
        this.metrics.intelligenceMetrics = {
          enabled: true,
          layersActive: 7,
          enhancementsApplied: 0,
          averageEnhancement: 0,
          falsePositivesReduced: 0,
          falseNegativesCaught: 0
        };
        
        console.log('✅ MULTI-LAYER INTELLIGENCE INITIALIZED: 7 layers active');
        console.log('🎯 Enhancement modes: Context validation, Geographic intelligence, Source credibility, Negative filtering, Emergency authorities, Age decay, Entity disambiguation');
        
      } else {
        console.log('🔤 MULTI-LAYER INTELLIGENCE DISABLED: Using basic keyword scoring only');
        this.multiLayerIntelligence = null;
        this.metrics.intelligenceMetrics = {
          enabled: false,
          reason: 'Disabled by configuration'
        };
      }
      
    } catch (error) {
      console.error('❌ CRITICAL ERROR: Failed to initialize multi-layer intelligence:', error);
      console.error('🔄 AUTOMATIC FALLBACK: Disabling multi-layer intelligence for this session');
      
      // SAFETY: Automatically disable if initialization fails
      this.config.useMultiLayerIntelligence = false;
      this.multiLayerIntelligence = null;
      
      // Add error tracking
      this.metrics.intelligenceMetrics = {
        enabled: false,
        initializationError: error.message,
        fallbackToBasicKeywords: true
      };
      
      console.log('🔤 FALLBACK ACTIVE: Using basic keyword scoring due to intelligence initialization failure');
    }
  }

  /**
   * 🧠 INITIALIZE GPT COMPONENTS (PHASE 3)
   * 
   * Dynamically loads GPT scoring components when feature flag is enabled.
   * Uses dynamic imports for optimal performance and safety.
   */
  async initializeGPTComponents() {
    try {
      console.log('🧠 INITIALIZING GPT COMPONENTS...');
      
      // Dynamic imports to avoid loading when not needed
      const { default: SmartContentTruncator } = await import('./smartContentTruncator.js');
      const { default: GPTRelevanceScorer } = await import('./gptRelevanceScorer.js');
      
      // Initialize components with configuration
      this.smartTruncator = new SmartContentTruncator({
        baseTokenLimit: this.config.gptTokenLimit || 300,
        contextWindowSize: this.config.gptContextWindow || 100,
        maxTotalTokens: this.config.gptMaxTokens || 500
      });
      
      this.gptScorer = new GPTRelevanceScorer({
        apiEndpoint: this.config.gptApiEndpoint,
        truncation: {
          baseTokenLimit: this.config.gptTokenLimit || 300,
          contextWindowSize: this.config.gptContextWindow || 100,
          maxTotalTokens: this.config.gptMaxTokens || 500
        }
      });
      
      // Add GPT metrics to system metrics
      this.metrics.gptMetrics = {
        initialized: true,
        initializationTime: Date.now(),
        processedArticles: 0,
        averageConfidence: 0,
        fallbackRate: 0,
        tokenSavings: 0
      };
      
      console.log('✅ GPT COMPONENTS INITIALIZED SUCCESSFULLY');
      console.log('🎯 Smart truncation active with fallback to keyword scoring');
      
    } catch (error) {
      console.error('❌ CRITICAL ERROR: Failed to initialize GPT components:', error);
      console.error('🔄 AUTOMATIC FALLBACK: Disabling GPT scoring for this session');
      
      // SAFETY: Automatically disable GPT scoring if initialization fails
      this.config.useGPTScoring = false;
      this.smartTruncator = null;
      this.gptScorer = null;
      
      // Add error tracking
      this.metrics.gptMetrics = {
        initialized: false,
        initializationError: error.message,
        fallbackToKeywords: true
      };
      
      console.log('🔤 FALLBACK ACTIVE: Using keyword-based scoring due to GPT initialization failure');
    }
  }

  /**
   * 🧠 BUILD CRISIS CONTEXT FOR INTELLIGENCE LAYERS
   * 
   * Extracts and formats crisis information for multi-layer intelligence processing.
   * Converts campaign options into standardized crisis context format.
   */
  buildCrisisContext() {
    try {
      const context = {
        type: 'weather', // Default to weather, can be enhanced later
        primaryLocation: null,
        relatedLocations: [],
        country: null,
        region: null,
        keywords: [],
        organizations: [],
        severity: 'medium'
      };

      // Extract from current campaign options
      if (this.currentCampaignOptions) {
        // Location information
        if (this.currentCampaignOptions.locations?.length > 0) {
          context.primaryLocation = this.currentCampaignOptions.locations[0];
          context.relatedLocations = this.currentCampaignOptions.locations.slice(1);
          
          // Infer country and region from primary location
          const locationInfo = this.inferLocationInfo(context.primaryLocation);
          context.country = locationInfo.country;
          context.region = locationInfo.region;
        }

        // Crisis type inference from keywords
        if (this.currentCampaignOptions.keywords?.length > 0) {
          context.keywords = this.currentCampaignOptions.keywords;
          context.type = this.inferCrisisType(this.currentCampaignOptions.keywords);
        }

        // Organization information
        if (this.currentCampaignOptions.organisations?.length > 0) {
          context.organizations = this.currentCampaignOptions.organisations;
        }

        // Severity inference
        if (this.currentCampaignOptions.crisisType) {
          context.type = this.currentCampaignOptions.crisisType;
        }
        if (this.currentCampaignOptions.severity) {
          context.severity = this.currentCampaignOptions.severity;
        }
      }

      console.log(`🎯 CRISIS CONTEXT: ${context.type} in ${context.primaryLocation || 'unknown location'}`);
      return context;

    } catch (error) {
      console.error('Error building crisis context:', error);
      return {
        type: 'weather',
        primaryLocation: 'Australia',
        relatedLocations: [],
        country: 'australia',
        region: null,
        keywords: ['emergency'],
        organizations: [],
        severity: 'medium'
      };
    }
  }

  /**
   * Infer location information from location string
   */
  inferLocationInfo(location) {
    if (!location) return { country: null, region: null };
    
    const loc = location.toLowerCase();
    
    // Australian locations
    if (['hunter', 'cessnock', 'newcastle', 'sydney', 'melbourne', 'brisbane', 'nsw', 'qld', 'vic'].some(term => loc.includes(term))) {
      return { 
        country: 'australia', 
        region: loc.includes('nsw') || loc.includes('hunter') || loc.includes('sydney') ? 'nsw' : 
                loc.includes('qld') || loc.includes('brisbane') ? 'qld' :
                loc.includes('vic') || loc.includes('melbourne') ? 'vic' : null
      };
    }
    
    // UK locations
    if (['london', 'manchester', 'birmingham', 'england', 'scotland', 'wales', 'uk'].some(term => loc.includes(term))) {
      return { country: 'uk', region: 'england' };
    }
    
    // US locations
    if (['new york', 'california', 'texas', 'florida', 'usa', 'america'].some(term => loc.includes(term))) {
      return { country: 'usa', region: null };
    }
    
    return { country: null, region: null };
  }

  /**
   * Infer crisis type from keywords
   */
  inferCrisisType(keywords) {
    if (!keywords || keywords.length === 0) return 'weather';
    
    const keywordString = keywords.join(' ').toLowerCase();
    
    // Weather-related
    if (['flood', 'storm', 'weather', 'rain', 'wind', 'cyclone', 'hurricane'].some(term => keywordString.includes(term))) {
      return 'weather';
    }
    
    // Missing person
    if (['missing', 'person', 'search', 'disappeared'].some(term => keywordString.includes(term))) {
      return 'missing_person';
    }
    
    // Terrorism/security
    if (['terror', 'attack', 'security', 'threat', 'bombing'].some(term => keywordString.includes(term))) {
      return 'terrorism';
    }
    
    // Fire-related
    if (['fire', 'bushfire', 'wildfire', 'blaze'].some(term => keywordString.includes(term))) {
      return 'wildfire';
    }
    
    // Earthquake
    if (['earthquake', 'tremor', 'seismic', 'quake'].some(term => keywordString.includes(term))) {
      return 'earthquake';
    }
    
    return 'weather'; // Default fallback
  }

  /**
   * Register a new feed provider
   */
  registerFeedProvider(provider) {
    if (!provider || typeof provider.fetchArticles !== 'function') {
      throw new Error('Invalid feed provider - must have fetchArticles method');
    }
    
    this.feedProviders.push(provider);
    console.log(`📡 Registered feed provider: ${provider.name}`);
    
    return this;
  }

  /**
   * Register an article processor
   */
  registerProcessor(processor) {
    if (!processor || typeof processor.process !== 'function') {
      throw new Error('Invalid processor - must have process method');
    }
    
    this.articleProcessors.push(processor);
    console.log(`⚙️ Registered processor: ${processor.name}`);
    
    return this;
  }

  /**
   * Start monitoring feeds
   */
  async start(intervalMinutes = 15) {
    if (this.isRunning) {
      console.log('⚠️ Media monitoring already running');
      return;
    }

    console.log(`🎬 Starting media monitoring (refresh every ${intervalMinutes}m)`);
    this.isRunning = true;

    // Initial run
    await this.runMonitoringCycle();

    // Set up periodic monitoring
    this.refreshInterval = setInterval(async () => {
      await this.runMonitoringCycle();
    }, intervalMinutes * 60 * 1000);

    return this;
  }

  /**
   * Stop monitoring
   */
  stop() {
    if (!this.isRunning) {
      return;
    }

    console.log('🛑 Stopping media monitoring');
    this.isRunning = false;

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }

    return this;
  }

  /**
   * Run a complete monitoring cycle with specific keywords and locations
   * 💰 COST-OPTIMIZED: Only processes feeds when active incidents exist
   */
  async runMonitoringCycle(campaignOptions = {}) {
    const startTime = performance.now();
    console.log('🔄 Running media monitoring cycle...');

    // Store campaign options for use during article filtering
    this.currentCampaignOptions = campaignOptions;

    // 🔍 PIPELINE DEBUG: Check for active incidents first
    console.log('🔍 STEP 1 - CHECKING FOR ACTIVE INCIDENTS...');
    const hasActiveIncidents = await this.checkForActiveIncidents();
    console.log(`🔍 RESULT: hasActiveIncidents = ${hasActiveIncidents}, forceRun = ${campaignOptions.forceRun}`);
    
    if (!hasActiveIncidents && !campaignOptions.forceRun) {
      console.log('🚫 PIPELINE BLOCKED: No active incidents found - skipping RSS feed processing to save GPT costs');
      console.log('💡 This could be why you see ZERO articles - the system thinks there are no active incidents!');
      console.log('💡 Tip: RSS feeds and GPT processing only run when there are active incidents in the system');
      this.isRunning = false;
      return {
        articlesProcessed: 0,
        message: 'Skipped - no active incidents (cost optimization)',
        hasActiveIncidents: false
      };
    }

    console.log(`✅ PIPELINE ACTIVE: ${hasActiveIncidents ? 'Active incidents detected' : 'Forced run'} - proceeding with RSS feed processing`);

    // SET RUNNING STATUS IMMEDIATELY
    this.isRunning = true;

    try {
      const allArticles = [];

      // Use provided keywords and locations, or fall back to generic monitoring
      const searchOptions = {
        keywords: campaignOptions.keywords || [],
        locations: campaignOptions.locations || [],
        organisations: campaignOptions.organisations || [],
        crisisType: campaignOptions.crisisType, // FIXED: Include crisisType for Google News ultra-specific logic
        severity: campaignOptions.severity,
        includeCrisisKeywords: campaignOptions.includeCrisisKeywords || false
      };

      console.log('🔍 Search Options:', searchOptions);

      // 🔍 STEP 2: FETCH FROM RSS PROVIDERS (🚀 PARALLEL OPTIMIZATION)
      console.log('🔍 STEP 2 - FETCHING FROM RSS PROVIDERS IN PARALLEL...');
      console.log(`🚀 PERFORMANCE OPTIMIZATION: Fetching from ${this.feedProviders.length} providers simultaneously`);
      
      // Create promises for all providers to fetch in parallel
      const fetchPromises = this.feedProviders.map(provider => 
        provider.fetchArticles(searchOptions)
          .then(articles => ({
            provider: provider,
            articles: articles,
            success: true
          }))
          .catch(error => ({
            provider: provider,
            articles: [],
            success: false,
            error: error
          }))
      );
      
      // Wait for all providers to complete (successful or failed)
      const fetchResults = await Promise.all(fetchPromises);
      
      // Process results with same logging as before
      for (const result of fetchResults) {
        if (result.success) {
          console.log(`🔍 ${result.provider.name} RAW FETCH: ${result.articles.length} articles`);
          
          // Log sample article titles for verification (preserved functionality)
          if (result.articles.length > 0) {
            console.log(`📰 Sample articles from ${result.provider.name}:`);
            result.articles.slice(0, 3).forEach((article, i) => {
              console.log(`   ${i+1}. "${article.title?.substring(0, 60)}..." (${article.publishedAt})`);
            });
          }
          
          allArticles.push(...result.articles);
          console.log(`✅ ${result.provider.name}: ${result.articles.length} articles added to total`);
        } else {
          console.error(`❌ Error fetching from ${result.provider.name}:`, result.error);
          this.metrics.errorCount++;
        }
      }

      console.log(`🔍 STEP 2 RESULT: ${allArticles.length} total articles fetched from all providers`);

      // 🔍 STEP 3: PROCESS ARTICLES
      console.log('🔍 STEP 3 - PROCESSING ARTICLES...');
      const processedArticles = await this.processArticles(allArticles);
      console.log(`🔍 STEP 3 RESULT: ${processedArticles.length} articles after processing (${allArticles.length - processedArticles.length} filtered out)`);

      // 🔍 STEP 4: STORE IN FIREBASE
      console.log('🔍 STEP 4 - STORING IN FIREBASE...');
      const storedCount = await this.storeArticles(processedArticles);
      console.log(`🔍 STEP 4 RESULT: ${storedCount} articles successfully stored (${processedArticles.length - storedCount} failed storage)`);

      // Update metrics
      this.metrics.articlesProcessed += processedArticles.length;
      this.metrics.lastRunTime = Date.now(); // Use timestamp instead of Date object
      this.metrics.processingTime = performance.now() - startTime;

      console.log(`✅ Monitoring cycle complete: ${storedCount} articles stored`);

      // KEEP RUNNING STATUS TRUE AFTER SUCCESSFUL COMPLETION
      // this.isRunning = true; // Already set above

    } catch (error) {
      console.error('❌ Error in monitoring cycle:', error);
      this.metrics.errorCount++;
      // Keep running even on error - don't set to false
    }
  }

  /**
   * Process articles through all registered processors
   */
  async processArticles(rawArticles) {
    const processed = [];
    
    console.log(`🔍 PROCESS ARTICLES: Starting with ${rawArticles.length} raw articles`);

    for (const article of rawArticles) {
      try {
        let processedArticle = { ...article };
        
        console.log(`🔍 Processing: "${article.title?.substring(0, 50)}..." (${article.publishedAt})`);

        // Run through all processors
        for (const processor of this.articleProcessors) {
          const beforeTitle = processedArticle.title?.substring(0, 30);
          processedArticle = await processor.process(processedArticle);
          const afterTitle = processedArticle.title?.substring(0, 30);
          console.log(`   📝 Processor ${processor.constructor.name}: "${beforeTitle}" → "${afterTitle}"`);
        }

        // Add processing metadata
        processedArticle.processedAt = new Date();
        processedArticle.id = this.generateArticleId(processedArticle);

        processed.push(processedArticle);
        console.log(`   ✅ Article processed successfully`);

      } catch (error) {
        console.error('❌ Error processing article:', error);
        this.metrics.errorCount++;
      }
    }

    console.log(`🔍 PROCESS ARTICLES RESULT: ${processed.length} articles processed successfully`);
    return processed;
  }

  /**
   * Store articles in Firebase with enhanced filtering and deduplication
   */
  async storeArticles(articles) {
    let storedCount = 0;
    let filteredCount = 0;
    let articleIndex = 0;

    // Limit articles per cycle to avoid overwhelming the system
    const articlesToProcess = articles.slice(0, this.config.maxArticlesPerCycle);
    
    console.log(`🔍 STORAGE TRACE: Starting with ${articles.length} total articles`);
    console.log(`🔍 STORAGE TRACE: Processing first ${articlesToProcess.length} articles (limit: ${this.config.maxArticlesPerCycle})`);
    
    if (articles.length > this.config.maxArticlesPerCycle) {
      console.log(`⚠️ Limiting to ${this.config.maxArticlesPerCycle} articles (${articles.length} total)`);
    }

    for (const article of articlesToProcess) {
      articleIndex++;
      
      // 🚨 ABC DIAGNOSTIC LOGGING - Track every ABC article
      const isABCArticle = article.source && article.source.toLowerCase().includes('abc news');
      if (isABCArticle) {
        console.log(`\n🚨 ABC DIAGNOSTIC: Processing ABC article ${articleIndex}/${articlesToProcess.length}`);
        console.log(`   📰 Title: "${article.title}"`);
        console.log(`   🏢 Source: "${article.source}"`);
        console.log(`   🌐 Feed Source: "${article.feedSource}"`);
        console.log(`   📅 Published: ${article.publishedAt}`);
        console.log(`   🔗 URL: ${article.url}`);
        console.log(`   📝 Content Preview: "${article.content?.substring(0, 200)}..."`);
        console.log(`   📍 Location: "${article.location}"`);
      }
      
      console.log(`\n🔍 ARTICLE ${articleIndex}/${articlesToProcess.length}: "${article.title?.substring(0, 60)}..." (${article.source || 'unknown source'})`);
      console.log(`   📅 Published: ${article.publishedAt || 'NO DATE'}`);
      console.log(`   🌐 Feed Source: ${article.feedSource || 'unknown feed'}`);
      console.log(`   🏢 Source: ${article.source || 'unknown'}`);
      console.log(`   🎯 Processing step by step...`);
      
      let exitReason = null;
      try {
        // AGGRESSIVE PRE-FILTERING: Block ALL demo and irrelevant content BEFORE storage
        // 🚨 COMPLETELY DISABLED FOR DEBUGGING ABC ARTICLE BLOCKING
        const shouldBlockCompletely = false;
        const shouldBlockCompletely_DISABLED = 
          // DEMO ARTICLES (NEVER STORE)
          article.isDemoArticle ||
          (article.feedSource && (
            article.feedSource.includes('Demo:') ||
            article.feedSource.includes('DEMO') ||
            article.feedSource.includes('demo') ||
            article.feedSource.includes('Demo - Crisis-Specific') ||
            article.feedSource.includes('Demo: Crisis-Specific')
          )) ||
          (article.url && article.url.includes('demo.crisis-system.com')) ||
          (article.title && (
            article.title.includes('Medical Emergency Response Team Deployed to Melbourne Office Building') ||
            article.title.includes('Ambulance Services Respond to Medical Incident at Exhibition Street Office') ||
            article.title.includes('Medical Emergency Response Team Deployed') ||
            article.title.includes('Ambulance Services Respond to Medical Incident')
          )) ||
          // NUCLEAR US CONTENT FILTERING (NEVER STORE)
          (article.title && (
            article.title.toLowerCase().includes('new york') ||
            article.title.toLowerCase().includes('california') ||
            article.title.toLowerCase().includes('texas') ||
            article.title.toLowerCase().includes('florida') ||
            article.title.toLowerCase().includes('illinois') ||
            article.title.toLowerCase().includes('washington') ||
            article.title.toLowerCase().includes('oregon') ||
            article.title.toLowerCase().includes('nevada') ||
            article.title.toLowerCase().includes('chicago') ||
            article.title.toLowerCase().includes('los angeles') ||
            article.title.toLowerCase().includes('san francisco') ||
            article.title.toLowerCase().includes('seattle') ||
            article.title.toLowerCase().includes('miami') ||
            article.title.toLowerCase().includes('boston') ||
            article.title.toLowerCase().includes('atlanta') ||
            article.title.toLowerCase().includes('denver') ||
            article.title.toLowerCase().includes('phoenix') ||
            article.title.toLowerCase().includes('detroit') ||
            article.title.toLowerCase().includes('philadelphia') ||
            article.title.toLowerCase().includes('dallas') ||
            article.title.toLowerCase().includes('houston') ||
            article.title.toLowerCase().includes('united states') ||
            article.title.toLowerCase().includes('us state') ||
            article.title.toLowerCase().includes('american') ||
            article.title.toLowerCase().includes('usa') ||
            article.title.toLowerCase().includes('u.s.') ||
            article.title.toLowerCase().includes('u.s') ||
            article.title.toLowerCase().includes('fda') ||
            article.title.toLowerCase().includes('cdc') ||
            article.title.toLowerCase().includes('pentagon') ||
            article.title.toLowerCase().includes('white house') ||
            article.title.toLowerCase().includes('congress') ||
            article.title.toLowerCase().includes('senate')
          )) ||
          (article.content && (
            article.content.toLowerCase().includes('new york') ||
            article.content.toLowerCase().includes('california') ||
            article.content.toLowerCase().includes('texas') ||
            article.content.toLowerCase().includes('united states') ||
            article.content.toLowerCase().includes('american') ||
            article.content.toLowerCase().includes('usa') ||
            article.content.toLowerCase().includes('u.s.')
          )) ||
          (article.location && (
            article.location.toLowerCase().includes('new york') ||
            article.location.toLowerCase().includes('california') ||
            article.location.toLowerCase().includes('texas') ||
            article.location.toLowerCase().includes('florida') ||
            article.location.toLowerCase().includes('usa') ||
            article.location.toLowerCase().includes('united states')
          )) ||
          (article.source && (
            article.source.toLowerCase().includes('cnn') ||
            article.source.toLowerCase().includes('fox news') ||
            article.source.toLowerCase().includes('nbc') ||
            article.source.toLowerCase().includes('cbs') ||
            (article.source.toLowerCase().includes('abc news') && article.source.toLowerCase().includes('united states')) ||
            article.source.toLowerCase().includes('usa today') ||
            article.source.toLowerCase().includes('new york times') ||
            article.source.toLowerCase().includes('washington post') ||
            article.source.toLowerCase().includes('wall street journal')
          )) ||
          // NUCLEAR CELEBRITY/ENTERTAINMENT FILTERING (NEVER STORE)
          (article.title && (
            article.title.toLowerCase().includes('miley cyrus') ||
            article.title.toLowerCase().includes('celebrity') ||
            article.title.toLowerCase().includes('kardashian') ||
            article.title.toLowerCase().includes('taylor swift') ||
            article.title.toLowerCase().includes('hollywood') ||
            article.title.toLowerCase().includes('movie') ||
            article.title.toLowerCase().includes('film') ||
            article.title.toLowerCase().includes('music') ||
            article.title.toLowerCase().includes('album') ||
            article.title.toLowerCase().includes('singer') ||
            article.title.toLowerCase().includes('actor') ||
            article.title.toLowerCase().includes('actress') ||
            article.title.toLowerCase().includes('pop star') ||
            article.title.toLowerCase().includes('rapper')
          )) ||
          // NUCLEAR SPORTS FILTERING (NEVER STORE)
          (article.title && (
            article.title.toLowerCase().includes('olympics') ||
            article.title.toLowerCase().includes('olympic') ||
            article.title.toLowerCase().includes('boxing') ||
            article.title.toLowerCase().includes('boxer') ||
            article.title.toLowerCase().includes('athlete') ||
            article.title.toLowerCase().includes('sport') ||
            article.title.toLowerCase().includes('team') ||
            article.title.toLowerCase().includes('match') ||
            article.title.toLowerCase().includes('game') ||
            article.title.toLowerCase().includes('tournament') ||
            article.title.toLowerCase().includes('championship') ||
            article.title.toLowerCase().includes('genetic screening') ||
            article.title.toLowerCase().includes('trans') ||
            article.title.toLowerCase().includes('gender')
          )) ||
          // NUCLEAR FOOD/EVENT FILTERING (NEVER STORE)
          (article.title && (
            article.title.toLowerCase().includes('food festival') ||
            article.title.toLowerCase().includes('chef') ||
            article.title.toLowerCase().includes('restaurant') ||
            article.title.toLowerCase().includes('festival') ||
            article.title.toLowerCase().includes('concert') ||
            article.title.toLowerCase().includes('party') ||
            article.title.toLowerCase().includes('celebration')
          )) ||
          // ENTERTAINMENT SOURCES (NEVER STORE)
          (article.source && (
            article.source.toLowerCase().includes('lovebscott') ||
            article.source.toLowerCase().includes('entertainment') ||
            article.source.toLowerCase().includes('tmz') ||
            article.source.toLowerCase().includes('people') ||
            article.source.toLowerCase().includes('eonline') ||
            article.source.toLowerCase().includes('hollywoodreporter') ||
            article.source.toLowerCase().includes('espn') ||
            article.source.toLowerCase().includes('sports')
          )) ||
          // CONTENT TYPE FILTERING (NEVER STORE)
          (article.contentType === 'sports') ||
          (article.contentType === 'entertainment') ||
          (article.contentType === 'celebrity') ||
          // MEDICAL BUT NOT WORKPLACE/BUSINESS CRISIS
          (article.title && article.title.toLowerCase().includes('medical') && 
           !article.title.toLowerCase().includes('fifteen lashes brewery') &&
           !article.title.toLowerCase().includes('workplace') &&
           !article.title.toLowerCase().includes('office') &&
           !article.title.toLowerCase().includes('business') &&
           !article.title.toLowerCase().includes('company'));

        if (shouldBlockCompletely) {
          filteredCount++;
          this.metrics.articlesFiltered++;
          
          // LOG SPECIFIC REASON FOR BLOCKING
          let blockReason = 'Unknown';
          if (article.isDemoArticle) blockReason = 'DEMO ARTICLE';
          else if (article.title?.toLowerCase().includes('new york') || article.title?.toLowerCase().includes('california')) blockReason = 'US CONTENT';
          else if (article.title?.toLowerCase().includes('miley cyrus') || article.title?.toLowerCase().includes('celebrity')) blockReason = 'CELEBRITY CONTENT';
          else if (article.title?.toLowerCase().includes('olympics') || article.title?.toLowerCase().includes('boxing')) blockReason = 'SPORTS CONTENT';
          else blockReason = 'IRRELEVANT CONTENT';
          
          // 🚨 ABC SPECIFIC BLOCKING ANALYSIS
          if (isABCArticle) {
            console.log(`\n🚨 ABC DIAGNOSTIC: ABC ARTICLE BLOCKED!`);
            console.log(`   📰 Blocked ABC Title: "${article.title}"`);
            console.log(`   🚫 Block Reason: ${blockReason}`);
            console.log(`   ⚗️ Detailed Filter Analysis:`);
            console.log(`      - isDemoArticle: ${article.isDemoArticle}`);
            console.log(`      - US title content: ${article.title?.toLowerCase().includes('new york') || article.title?.toLowerCase().includes('california') || article.title?.toLowerCase().includes('texas')}`);
            console.log(`      - Celebrity content: ${article.title?.toLowerCase().includes('miley cyrus') || article.title?.toLowerCase().includes('celebrity')}`);
            console.log(`      - Sports content: ${article.title?.toLowerCase().includes('olympics') || article.title?.toLowerCase().includes('boxing')}`);
            console.log(`      - Source filter match: ${(article.source.toLowerCase().includes('abc news') && article.source.toLowerCase().includes('united states'))}`);
            console.log(`      - Entertainment content: ${article.title?.toLowerCase().includes('entertainment')}`);
            console.log(`   🔍 Full shouldBlockCompletely evaluation: ${shouldBlockCompletely}`);
          }
          
          exitReason = `BLOCKED_PRE_STORAGE (${blockReason})`;
          console.log(`   🚫 EXIT REASON: ${exitReason} - "${article.title?.substring(0, 60)}..."`);
          filteredCount++;
          continue; // SKIP completely - don't even process further
        }

        // Skip articles that were filtered out by processors
        if (this.config.enableAdvancedFiltering && article.filtered) {
          exitReason = `PROCESSOR_FILTERED (${article.filterReason})`;
          console.log(`   🚫 EXIT REASON: ${exitReason}`);
          filteredCount++;
          this.metrics.articlesFiltered++;
          
          // Optionally store filtered articles for analysis
          if (this.config.storeFilteredArticles) {
            await this.storeFilteredArticle(article);
          }
          
          continue;
        }

        // 🎯 DUAL SCORING SYSTEM: GPT + KEYWORD FALLBACK
        let relevanceScore = 0;
        let relevanceReasons = [];
        let scoringMethod = 'KEYWORD'; // Track which method was used
        let gptResult = null;
        
        const articleTitle = (article.title || '').toLowerCase();
        const articleContent = (article.content || '').toLowerCase();
        const articleText = `${articleTitle} ${articleContent}`;
        
        console.log(`🎯 SCORING ARTICLE: "${article.title?.substring(0, 50)}..."`);
        
        // 🧠 PHASE 3: GPT SCORING PATH (WHEN ENABLED)
        if (this.config.useGPTScoring && this.gptScorer && this.smartTruncator) {
          try {
            console.log('🧠 USING GPT SCORING: Processing with AI-powered relevance assessment...');
            
            // Build incident context for GPT
            const incidentContext = this.currentCampaignOptions ? {
              type: this.currentCampaignOptions.crisisType || 'general emergency',
              location: this.currentCampaignOptions.location || 'Australia',
              keywords: this.currentCampaignOptions.keywords || [],
              organisations: this.currentCampaignOptions.organisations || [],
              severity: this.currentCampaignOptions.severity || 'medium'
            } : null;
            
            // Score with GPT
            gptResult = await this.gptScorer.scoreArticleRelevance(article, incidentContext);
            
            if (gptResult && typeof gptResult.relevanceScore === 'number') {
              relevanceScore = gptResult.relevanceScore; // Already 0-1 scale
              relevanceReasons.push(`GPT Assessment: ${gptResult.relevanceExplanation || 'AI-powered analysis'}`);
              scoringMethod = 'GPT';
              
              // Update GPT metrics
              if (this.metrics.gptMetrics) {
                this.metrics.gptMetrics.processedArticles++;
                this.metrics.gptMetrics.averageConfidence = 
                  (this.metrics.gptMetrics.averageConfidence * (this.metrics.gptMetrics.processedArticles - 1) + 
                   (gptResult.gptProcessing?.confidenceLevel || 50)) / this.metrics.gptMetrics.processedArticles;
                
                if (gptResult.gptProcessing?.fallbackQueued) {
                  this.metrics.gptMetrics.fallbackRate = 
                    (this.metrics.gptMetrics.fallbackRate * (this.metrics.gptMetrics.processedArticles - 1) + 1) / 
                    this.metrics.gptMetrics.processedArticles;
                }
                
                if (gptResult.gptProcessing?.tokenSavings) {
                  this.metrics.gptMetrics.tokenSavings = 
                    (this.metrics.gptMetrics.tokenSavings * (this.metrics.gptMetrics.processedArticles - 1) + 
                     gptResult.gptProcessing.tokenSavings) / this.metrics.gptMetrics.processedArticles;
                }
              }
              
              console.log(`🧠 GPT RESULT: ${Math.round(relevanceScore * 100)}% relevance`);
              console.log(`🧠 GPT DETAILS: ${gptResult.gptProcessing?.tokenSavings || 0}% tokens saved, ` +
                         `${gptResult.gptProcessing?.confidenceLevel || 0}% confidence`);
              
            } else {
              throw new Error('Invalid GPT response format');
            }
            
          } catch (error) {
            console.error(`❌ GPT SCORING FAILED: ${error.message}`);
            console.log('🔄 AUTOMATIC FALLBACK: Using keyword-based scoring for this article');
            
            // Clear GPT result and proceed to keyword scoring
            gptResult = null;
            relevanceScore = 0;
            relevanceReasons = [];
            scoringMethod = 'KEYWORD_FALLBACK';
          }
        }
        
        // 🔤 KEYWORD SCORING PATH (DEFAULT OR FALLBACK)
        if (!this.config.useGPTScoring || !gptResult) {
          if (this.config.useGPTScoring) {
            console.log('🔤 FALLBACK MODE: GPT failed, using keyword-based scoring');
          } else {
            console.log('🔤 KEYWORD MODE: Using traditional keyword-based scoring (default)');
          }
          
          // SCORE 1: CRISIS TYPE RELEVANCE (HIGH PRIORITY)
        const crisisTypeWeights = {
          "missing": 15,
          "missing person": 20,
          "missing woman": 20,
          "missing man": 20,
          "swept away": 18,
          "floodwater": 15,
          "floodwaters": 15,
          "flood": 12,
          "flooding": 12,
          "severe weather": 15,
          "wild weather": 12,
          "storm": 10,
          "emergency": 8,
          "search": 10,
          "rescue": 10
        };
        
        let crisisTypeScore = 0;
        let matchedCrisisTerms = [];
        
        Object.entries(crisisTypeWeights).forEach(([term, weight]) => {
          if (articleTitle.includes(term)) {
            crisisTypeScore += weight + 5; // Extra bonus for title mention
            matchedCrisisTerms.push(`${term}(title:+${weight + 5})`);
            console.log(`🚨 CRISIS TYPE IN TITLE: "${term}" +${weight + 5} points`);
          } else if (articleContent.includes(term)) {
            crisisTypeScore += weight;
            matchedCrisisTerms.push(`${term}(content:+${weight})`);
            console.log(`🚨 CRISIS TYPE IN CONTENT: "${term}" +${weight} points`);
          }
        });
        
        relevanceScore += crisisTypeScore;
        if (matchedCrisisTerms.length > 0) {
          relevanceReasons.push(`crisis terms: ${matchedCrisisTerms.join(', ')}`);
        }
        
        // SCORE 2: LOCATION HIERARCHY (ENHANCED)
        if (this.currentCampaignOptions?.locations?.length > 0) {
          for (const location of this.currentCampaignOptions.locations) {
            let locationScore = 0;
            let locationMatches = [];
            
            // Direct location match
            const locationWords = location.toLowerCase().split(/[,\s]+/).filter(word => word.length > 2);
            
            // Enhanced location hierarchy for NSW
            const locationHierarchy = {
              'cessnock': ['hunter', 'hunter valley', 'hunter region', 'nsw', 'new south wales'],
              'nsw': ['new south wales', 'sydney', 'newcastle', 'hunter', 'central coast'],
              'hunter': ['cessnock', 'newcastle', 'maitland', 'singleton', 'hunter valley']
            };
            
            // Check direct location words
            locationWords.forEach(word => {
              if (articleTitle.includes(word)) {
                locationScore += 15; // High score for title location
                locationMatches.push(`${word}(title:+15)`);
                console.log(`📍 LOCATION IN TITLE: "${word}" +15 points`);
              } else if (articleContent.includes(word)) {
                locationScore += 8; // Medium score for content location
                locationMatches.push(`${word}(content:+8)`);
                console.log(`📍 LOCATION IN CONTENT: "${word}" +8 points`);
              }
            });
            
            // Check location hierarchy
            locationWords.forEach(locationWord => {
              const relatedLocations = locationHierarchy[locationWord] || [];
              relatedLocations.forEach(related => {
                if (articleTitle.includes(related)) {
                  locationScore += 12; // Good score for related location in title
                  locationMatches.push(`${related}(hierarchy-title:+12)`);
                  console.log(`📍 RELATED LOCATION IN TITLE: "${related}" +12 points`);
                } else if (articleContent.includes(related)) {
                  locationScore += 6; // Some score for related location in content
                  locationMatches.push(`${related}(hierarchy-content:+6)`);
                  console.log(`📍 RELATED LOCATION IN CONTENT: "${related}" +6 points`);
                }
              });
            });
            
            relevanceScore += locationScore;
            if (locationMatches.length > 0) {
              relevanceReasons.push(`location: ${locationMatches.join(', ')}`);
            }
          }
        }
        
        // SCORE 3: ORGANIZATION MENTION (BONUS, NOT REQUIRED)
        if (this.currentCampaignOptions?.organisations?.length > 0) {
          for (const orgName of this.currentCampaignOptions.organisations) {
            if (orgName && (
              articleTitle.includes(orgName.toLowerCase()) ||
              articleContent.includes(orgName.toLowerCase())
            )) {
              relevanceScore += 25; // Reduced from 30 to balance scoring
              relevanceReasons.push(`mentions "${orgName}"`);
              console.log(`🏢 ORG BONUS: +25 points for mentioning "${orgName}"`);
              break;
            }
          }
        }
        
        // SCORE 4: ADDITIONAL CRISIS KEYWORDS
        const additionalCrisisWords = ['ses', 'rfs', 'police', 'ambulance', 'evacuation', 'warning', 'alert', 'danger', 'urgent'];
        let additionalMatches = 0;
        additionalCrisisWords.forEach(word => {
          if (articleText.includes(word)) {
            additionalMatches++;
          }
        });
        if (additionalMatches > 0) {
          const additionalScore = Math.min(additionalMatches * 3, 15); // Up to 15 points
          relevanceScore += additionalScore;
          relevanceReasons.push(`${additionalMatches} additional crisis keywords`);
          console.log(`🚨 ADDITIONAL CRISIS KEYWORDS: +${additionalScore} points for ${additionalMatches} words`);
        }
        
        // SCORE 4: Article age scoring with HARD CUTOFF for ancient articles
        if (article.publishedAt) {
          const articleDate = new Date(article.publishedAt);
          const now = Date.now();
          const articleAge = now - articleDate.getTime();
          const hoursOld = articleAge / (1000 * 60 * 60);
          const daysOld = hoursOld / 24;
          
          console.log(`📅 DATE DEBUG: Article "${article.title?.substring(0, 40)}..." published: ${articleDate.toISOString()} (${daysOld.toFixed(1)} days ago)`);
          
          // 🔧 DEBUGGING: AGE FILTER COMPLETELY DISABLED
          console.log(`🔧 DEBUG MODE: AGE FILTER DISABLED - Article ${daysOld.toFixed(1)} days old will be allowed`);
          
          // if (daysOld > 90) { // Changed from 30 to 90 days for testing
          //   console.log(`🚫 ANCIENT ARTICLE BLOCKED: ${daysOld.toFixed(1)} days old (>90 day limit) - "${article.title?.substring(0, 50)}..."`);
          //   filteredCount++;
          //   this.metrics.articlesFiltered++;
          //   continue; // Skip this article entirely
          // } else if (daysOld > 30) {
          //   console.log(`⚠️ OLD ARTICLE (but allowing for debug): ${daysOld.toFixed(1)} days old - "${article.title?.substring(0, 50)}..."`);
          // }
          
          if (hoursOld < 24) {
            // Recent article bonus (within 24 hours)
            relevanceScore += 10;
            relevanceReasons.push('fresh article (<24h)');
            console.log(`✅ FRESH: +10 points for very recent article (${hoursOld.toFixed(1)}h old)`);
          } else if (daysOld <= 7) {
            // Still recent (within 7 days) - small bonus
            relevanceScore += 2;
            relevanceReasons.push('recent article (<7d)');
            console.log(`✅ RECENT: +2 points for recent article (${daysOld.toFixed(1)}d old)`);
          } else if (daysOld <= 14) {
            // Moderately old (7-14 days) - small penalty
            const agePenalty = Math.floor(daysOld - 7) * 3; // -3 points per day over 7 days
            relevanceScore -= agePenalty;
            relevanceReasons.push(`moderate age penalty (-${agePenalty} pts)`);
            console.log(`⚠️ MODERATE AGE PENALTY: -${agePenalty} points for article (${daysOld.toFixed(1)}d old)`);
          } else {
            // OLD ARTICLE MASSIVE PENALTY: Articles 14-30 days old get severe penalties
            const basePenalty = 7 * 3; // First 7 days over week = -21 points
            const severePenalty = Math.floor(daysOld - 14) * 5; // -5 points per day over 14 days
            const totalPenalty = basePenalty + severePenalty;
            relevanceScore -= totalPenalty;
            relevanceReasons.push(`severe age penalty (-${totalPenalty} pts)`);
            console.log(`❌ SEVERE AGE PENALTY: -${totalPenalty} points for old article (${daysOld.toFixed(1)}d old)`);
          }
        } else {
          // 🔧 DEBUGGING: NO-DATE FILTER DISABLED
          console.log(`🔧 DEBUG MODE: NO-DATE FILTER DISABLED - Article "${article.title?.substring(0, 40)}..." without date will be allowed`);
          
          // console.log(`⚠️ NO DATE: Article "${article.title?.substring(0, 40)}..." has no publishedAt date - BLOCKED for safety`);
          // // Block articles with no date for safety
          // filteredCount++;
          // this.metrics.articlesFiltered++;
          // continue;
        }
          
          // 🧠 APPLY MULTI-LAYER INTELLIGENCE ENHANCEMENT
          if (this.config.useMultiLayerIntelligence && this.multiLayerIntelligence) {
            console.log(`🧠 APPLYING MULTI-LAYER ENHANCEMENT: Base score ${relevanceScore} points`);
            
            try {
              // Build crisis context for intelligence layers
              const crisisContext = this.buildCrisisContext();
              
              // Store original score for metrics calculation
              const originalScore = relevanceScore;
              
              // Apply enhancement layers to raw keyword score
              const enhancementResult = this.multiLayerIntelligence.enhanceRelevanceScore(
                relevanceScore, 
                relevanceReasons, 
                article, 
                crisisContext, 
                this.currentCampaignOptions
              );
              
              // Update score and reasons with enhancements
              relevanceScore = enhancementResult.enhancedScore;
              relevanceReasons = enhancementResult.enhancementReasons;
              
              // Update intelligence metrics
              if (this.metrics.intelligenceMetrics) {
                this.metrics.intelligenceMetrics.enhancementsApplied++;
                const enhancement = enhancementResult.enhancedScore - originalScore;
                this.metrics.intelligenceMetrics.averageEnhancement = 
                  (this.metrics.intelligenceMetrics.averageEnhancement * (this.metrics.intelligenceMetrics.enhancementsApplied - 1) + enhancement) / 
                  this.metrics.intelligenceMetrics.enhancementsApplied;
              }
              
              console.log(`🧠 ENHANCEMENT APPLIED: ${enhancementResult.layersProcessed} layers, ${enhancementResult.appliedEnhancements.join(', ')}`);
              scoringMethod = scoringMethod === 'KEYWORD_FALLBACK' ? 'KEYWORD_ENHANCED_FALLBACK' : 'KEYWORD_ENHANCED';
              
            } catch (error) {
              console.error('❌ Multi-layer enhancement failed:', error.message);
              console.log('🔄 CONTINUING: Using base keyword score without enhancement');
              scoringMethod = scoringMethod === 'KEYWORD_FALLBACK' ? 'KEYWORD_FALLBACK' : 'KEYWORD';
            }
          }
          
          // Convert final score to 0-1 scale for consistency with GPT
          const maxPossibleScore = 120;
          relevanceScore = Math.min(relevanceScore / maxPossibleScore, 1.0);
        }
        
        // 📊 FINAL SCORING SUMMARY (BOTH GPT AND KEYWORD MODES)
        article.relevanceScore = relevanceScore; // Already 0-1 scale for both methods
        article.scoringMethod = scoringMethod; // Track which method was used
        
        // Enhanced logging with method indication
        console.log(`📊 FINAL SCORE (${scoringMethod}): ${Math.round(article.relevanceScore * 100)}% relevance`);
        console.log(`📋 SCORE BREAKDOWN: ${relevanceReasons.join(' | ')}`);
        
        // Add GPT-specific logging if GPT was used
        if (gptResult && gptResult.gptProcessing) {
          console.log(`🧠 GPT PROCESSING: ${gptResult.gptProcessing.tokenSavings}% tokens saved, ` +
                     `${gptResult.gptProcessing.confidenceLevel}% confidence`);
          if (gptResult.gptProcessing.fallbackQueued) {
            console.log(`🔄 CONFIDENCE FALLBACK: Queued for full-content reprocessing`);
          }
        }
        
        // APPLY DYNAMIC THRESHOLD based on user sensitivity - NOW USES PERCENTAGE THRESHOLDS
        const percentageThreshold = this.getRelevanceThreshold(); // Gets 0.75, 0.50, or 0.30
        
        console.log(`🔍 FILTERING ARTICLE: "${article.title?.substring(0, 40)}..."`);
        console.log(`   📊 Article relevance: ${Math.round(article.relevanceScore * 100)}%`);
        console.log(`   🎚️ Required threshold: ${Math.round(percentageThreshold * 100)}% (${this.config.filterSensitivity} mode)`);
        console.log(`   🔧 Raw config.filterSensitivity: "${this.config.filterSensitivity}"`);
        
        // ✅ RELEVANCE FILTER RE-ENABLED: Medium = 50%+, Strict = 75%+, Relaxed = 30%+
        if (article.relevanceScore < percentageThreshold) {
          filteredCount++;
          this.metrics.articlesFiltered++;
          console.log(`   🚫 FILTERED OUT: ${Math.round(article.relevanceScore * 100)}% < ${Math.round(percentageThreshold * 100)}% required`);
          continue;
        } else {
          console.log(`   ✅ PASSES FILTER: ${Math.round(article.relevanceScore * 100)}% ≥ ${Math.round(percentageThreshold * 100)}% - ${relevanceReasons.join(', ')}`);
        }

        // ✅ DUPLICATE DETECTION PERMANENTLY REMOVED
        // All articles are now stored and managed via clustering system

        // 🗄️ STORE IN FIREBASE DATABASE
        const articlesRef = collection(this.db, 'c3_media_articles');
        
        // Prepare article for database storage
        const articleForStorage = {
          ...article,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          orgId: this.currentCampaignOptions?.orgId || 'unknown', // 🔧 FIX: Store with organization ID
          chatTriggered: false // 🔧 FIX: Track if article has triggered live chat to prevent spam
        };
        
        console.log(`💾 STORING ARTICLE: "${article.title?.substring(0, 50)}..." to c3_media_articles`);
        console.log(`📊 Storage data: relevance=${Math.round(article.relevanceScore * 100)}%, source=${article.source}, orgId=${articleForStorage.orgId}, publishedAt=${article.publishedAt}`);
        console.log(`🔍 STORAGE TRACE: Will this article trigger live chat? ${article.relevanceScore >= 0.95 ? 'YES ✅' : 'NO ❌'} (score: ${article.relevanceScore.toFixed(3)})`);
        
        const docRef = await addDoc(articlesRef, articleForStorage);
        
        console.log(`✅ STORED SUCCESSFULLY: Firestore ID=${docRef.id}`);
        
        // 🚨 ABC SUCCESS LOGGING
        if (isABCArticle) {
          console.log(`\n🎉 ABC DIAGNOSTIC: ABC ARTICLE SUCCESSFULLY STORED!`);
          console.log(`   📰 Stored ABC Title: "${article.title}"`);
          console.log(`   🆔 Firestore ID: ${docRef.id}`);
          console.log(`   📊 Relevance Score: ${Math.round((article.relevanceScore || 0) * 100)}%`);
          console.log(`   🎯 Article Type: ${article.articleType || 'unknown'}`);
          console.log(`   🕐 Published: ${article.publishedAt}`);
          console.log(`   🔗 URL: ${article.url}`);
        }

        // FORENSIC LOGGING: Log media article detection and storage
        try {
          // Log to all active organisations/incidents (no specific org context available)
          await createIncidentLog({
            incidentId: 'system', // System-level activity
            orgId: 'global', // Global media monitoring
            action: ACTIVITY_TYPES.MEDIA_ARTICLE_STORED,
            details: `Media article stored: "${article.title}" (Relevance: ${(article.relevanceScore * 100).toFixed(0)}%)`,
            userId: 'system',
            userEmail: 'system@c3.com',
            userFullName: 'Media Intelligence System',
            priority: article.relevanceScore >= 0.98 ? 'high' : 'normal',
            metadata: {
              articleId: docRef.id,
              articleUrl: article.url,
              relevanceScore: article.relevanceScore,
              contentType: article.contentType,
              source: article.source,
              crisisIndicators: article.crisisIndicators || [],
              publishedAt: article.publishedAt
            }
          });
        } catch (logError) {
          console.error('Error logging media article storage:', logError);
          // Don't fail article storage if logging fails
        }

        storedCount++;
        
        // Track relevance scores for metrics
        this.metrics.relevanceScores.push(article.relevanceScore);

        exitReason = `STORED_SUCCESSFULLY`;
        console.log(`   ✅ EXIT REASON: ${exitReason} - Relevance: ${article.relevanceScore.toFixed(2)}, Type: ${article.contentType}`);
        console.log(`   📝 Stored article: "${article.title?.substring(0, 60)}..."`);

        // Check if this article should trigger alerts
        await this.checkForAlerts(article);

        // SEND SYSTEM CHAT MESSAGE for high-relevance articles (with deduplication)
        if (article.relevanceScore >= 0.95 && !article.chatTriggered) {
          console.log(`💬 TRIGGERING LIVE CHAT: Article "${article.title?.substring(0, 50)}..." with ${Math.round(article.relevanceScore * 100)}% relevance`);
          console.log(`💬 CHAT ARTICLE DETAILS: orgId=${article.orgId || 'MISSING'}, source=${article.source}, publishedAt=${article.publishedAt}`);
          
          try {
          await this.sendSystemChatMessage(article);
            
            // 🔧 Mark article as having triggered chat to prevent spam on refresh
            try {
              const articleDocRef = doc(this.db, 'c3_media_articles', docRef.id);
              await updateDoc(articleDocRef, { chatTriggered: true });
              console.log(`✅ CHAT DEDUPLICATION: Marked article ${docRef.id} as chat-triggered`);
            } catch (updateError) {
              console.warn(`⚠️ Failed to update chatTriggered flag for article ${docRef.id}:`, updateError.message);
              // Don't fail the entire process if this update fails
            }
          } catch (chatError) {
            console.error(`❌ Failed to send chat message for article "${article.title?.substring(0, 50)}...":`, chatError.message);
            // Don't fail the entire process if chat fails
          }
        } else if (article.relevanceScore >= 0.95 && article.chatTriggered) {
          console.log(`🔇 CHAT SKIPPED: Article "${article.title?.substring(0, 50)}..." already triggered chat (deduplication)`);
        }

      } catch (error) {
        exitReason = `STORAGE_ERROR (${error.code || error.message})`;
        console.log(`   ❌ EXIT REASON: ${exitReason} - "${article.title?.substring(0, 60)}..."`);
        console.error('❌ Full error details:', error);
        this.metrics.errorCount++;
        
        // For permission errors, log but don't fail the entire operation
        if (error.code === 'permission-denied') {
          console.warn('⚠️ Permission denied for storing article - check Firestore rules');
        }
      }
    }

    console.log(`\n🔧 DEBUG MODE ACTIVE: ALL FILTERS DISABLED FOR TESTING`);
    console.log(`📊 Processing complete: ${storedCount} stored, ${filteredCount} filtered`);
    console.log(`🎯 Expected: Should see ~306 articles stored (all from RSS feeds) with filters disabled`);
    console.log(`📈 Total articles processed: ${articleIndex} out of ${articlesToProcess.length} (${articles.length} total fetched)`);
    
    const processedButNotStored = articleIndex - storedCount;
    if (processedButNotStored > 0) {
      console.log(`⚠️ ATTENTION: ${processedButNotStored} articles were processed but not stored!`);
      console.log(`   🔍 Check the individual EXIT REASON logs above to see why each was rejected`);
    }
    
    return storedCount;
  }

  /**
   * Store filtered articles for analysis (debugging purposes)
   */
  async storeFilteredArticle(article) {
    try {
      const filteredRef = collection(this.db, 'c3_media_articles_filtered');
      await addDoc(filteredRef, {
        ...article,
        filteredAt: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ Error storing filtered article:', error);
    }
  }

  /**
   * DEPRECATED: Duplicate detection has been replaced by clustering system
   * This method is kept as a stub for compatibility but always returns false
   */
  async isDuplicateArticle(article) {
    // ✅ Duplicate detection permanently disabled - clustering handles similar articles
    return false;
  }

  /**
   * LEGACY METHOD - Keep for any remaining references
   */
  async LEGACY_isDuplicateArticle_DISABLED(article) {
    try {
      console.log(`🔍 DUPLICATE CHECK: Starting for "${article.title?.substring(0, 40)}..."`);
      const articlesRef = collection(this.db, 'c3_media_articles');
      
      // Method 1: Check for exact URL match (fastest)
      if (article.url) {
        try {
          console.log(`🔍 Checking URL duplicates: ${article.url?.substring(0, 50)}...`);
          const urlQuery = query(
        articlesRef,
        where('url', '==', article.url),
        limit(1)
      );
          const urlSnapshot = await getDocs(urlQuery);
          if (!urlSnapshot.empty) {
            console.log(`🔄 Duplicate detected by URL: ${article.url}`);
            return true;
          }
          console.log(`✅ No URL duplicates found`);
        } catch (urlError) {
          console.warn(`⚠️ URL duplicate check failed: ${urlError.message}`);
          // Continue to other checks
        }
      }

      // Method 2: Check for GUID match (for RSS feeds)
      if (article.guid) {
        try {
          console.log(`🔍 Checking GUID duplicates: ${article.guid?.substring(0, 50)}...`);
          const guidQuery = query(
            articlesRef,
            where('guid', '==', article.guid),
            limit(1)
          );
          const guidSnapshot = await getDocs(guidQuery);
          if (!guidSnapshot.empty) {
            console.log(`🔄 Duplicate detected by GUID: ${article.guid}`);
            return true;
          }
          console.log(`✅ No GUID duplicates found`);
        } catch (guidError) {
          console.warn(`⚠️ GUID duplicate check failed: ${guidError.message}`);
          // Continue to other checks
        }
      }

      // Method 3: Check for similar title within same timeframe (cross-feed detection)
      if (article.title && article.publishedAt) {
        try {
          const titleWords = this.extractKeyWordsFromTitle(article.title);
          if (titleWords.length >= 3) { // Only check if title has substantial content
            
            console.log(`🔍 Checking title similarity duplicates...`);
            // Look for articles with similar titles published within 24 hours
            const twentyFourHoursAgo = new Date(article.publishedAt.getTime() - (24 * 60 * 60 * 1000));
            const twentyFourHoursLater = new Date(article.publishedAt.getTime() + (24 * 60 * 60 * 1000));
            
            const similarTitleQuery = query(
              articlesRef,
              where('publishedAt', '>=', twentyFourHoursAgo),
              where('publishedAt', '<=', twentyFourHoursLater),
              limit(20) // Check recent articles only
            );
            
            const similarSnapshot = await getDocs(similarTitleQuery);
            console.log(`🔍 Found ${similarSnapshot.docs.length} recent articles to compare titles with`);
            
            for (const existingDoc of similarSnapshot.docs) {
              const existingArticle = existingDoc.data();
              if (this.areTitlesSimilar(article.title, existingArticle.title)) {
                console.log(`🔄 Cross-feed duplicate detected by title similarity:`);
                console.log(`   New: "${article.title}" (${article.source})`);
                console.log(`   Existing: "${existingArticle.title}" (${existingArticle.source})`);
                return true;
              }
            }
            console.log(`✅ No title similarity duplicates found`);
          } else {
            console.log(`⚠️ Title too short for similarity checking: "${article.title}"`);
          }
        } catch (titleError) {
          console.warn(`⚠️ Title similarity check failed: ${titleError.message}`);
          // This is often due to missing Firestore indexes for date range queries
          if (titleError.message?.includes('index')) {
            console.warn(`📋 Firestore index needed for publishedAt range queries. See Firebase Console.`);
          }
        }
      }

      console.log(`✅ DUPLICATE CHECK COMPLETE: No duplicates found for "${article.title?.substring(0, 40)}..."`);
      return false;

    } catch (error) {
      console.error('❌ Error checking duplicates:', error);
      console.error('❌ Full error details:', error);
      
      // For permission errors, provide specific guidance
      if (error.code === 'permission-denied') {
        console.warn('⚠️ Permission denied for checking duplicates - Firestore rules may need updating');
        console.warn('⚠️ Article will be stored without duplicate checking to avoid data loss');
      } else if (error.message?.includes('index')) {
        console.warn('⚠️ Missing Firestore index - some duplicate checks skipped');
        console.warn('⚠️ Please create required indexes in Firebase Console');
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        console.warn('⚠️ Network error during duplicate check - will proceed without full checking');
      }
      
      // Always return false on error - better to have duplicates than miss articles
      return false;
    }
  }

  /**
   * Extract key words from title for similarity comparison
   */
  extractKeyWordsFromTitle(title) {
    if (!title) return [];
    
    // Remove common words and extract meaningful terms
    const stopWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'having', 'do', 'does', 'did', 'will', 'would', 'could', 'should'];
    
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove punctuation
      .split(/\s+/)
      .filter(word => word.length > 2 && !stopWords.includes(word))
      .slice(0, 10); // Take first 10 meaningful words
  }

  /**
   * Check if two titles are similar enough to be considered duplicates
   */
  areTitlesSimilar(title1, title2) {
    if (!title1 || !title2) return false;
    
    const words1 = this.extractKeyWordsFromTitle(title1);
    const words2 = this.extractKeyWordsFromTitle(title2);
    
    if (words1.length === 0 || words2.length === 0) return false;
    
    // Calculate word overlap percentage
    const commonWords = words1.filter(word => words2.includes(word));
    const totalUniqueWords = new Set([...words1, ...words2]).size;
    const overlapPercentage = commonWords.length / Math.min(words1.length, words2.length);
    
    // Consider similar if 70% or more words overlap and at least 3 common words
    return overlapPercentage >= 0.7 && commonWords.length >= 3;
  }

  /**
   * Check if article should trigger alerts
   */
  async checkForAlerts(article) {
    try {
      // Get active monitoring campaigns
      const campaigns = await this.getActiveMonitoringCampaigns();

      for (const campaign of campaigns) {
        const shouldAlert = this.evaluateAlertRules(article, campaign);
        
        if (shouldAlert) {
          await this.createAlert(article, campaign);
          this.metrics.alertsGenerated++;
        }
      }

    } catch (error) {
      console.error('❌ Error checking alerts:', error);
    }
  }

  /**
   * Get active monitoring campaigns for organisations
   */
  async getActiveMonitoringCampaigns() {
    try {
      const campaignsRef = collection(this.db, 'c3_media_campaigns');
      const activeQuery = query(
        campaignsRef,
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(activeQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

    } catch (error) {
      console.error('❌ Error fetching campaigns:', error);
      return [];
    }
  }

  /**
   * Evaluate if article matches alert rules
   */
  evaluateAlertRules(article, campaign) {
    const rules = campaign.alertRules || {};
    
    // Check organization name mentions
    if (rules.organizationMentions) {
      const orgNames = rules.organizationMentions.map(name => name.toLowerCase());
      const articleText = `${article.title} ${article.content || ''}`.toLowerCase();
      
      for (const orgName of orgNames) {
        if (articleText.includes(orgName)) {
          return true;
        }
      }
    }

    // Check keywords
    if (rules.keywords && rules.keywords.length > 0) {
      const keywords = rules.keywords.map(kw => kw.toLowerCase());
      const articleText = `${article.title} ${article.content || ''}`.toLowerCase();
      
      for (const keyword of keywords) {
        if (articleText.includes(keyword)) {
          return true;
        }
      }
    }

    // Check location matching
    if (rules.locations && rules.locations.length > 0 && article.location) {
      const locations = rules.locations.map(loc => loc.toLowerCase());
      const articleLocation = article.location.toLowerCase();
      
      for (const location of locations) {
        if (articleLocation.includes(location)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Create alert in Firebase
   */
  async createAlert(article, campaign) {
    try {
      const alertsRef = collection(this.db, 'c3_media_alerts');
      
      await addDoc(alertsRef, {
        articleId: article.id,
        campaignId: campaign.id,
        orgId: campaign.orgId,
        incidentId: campaign.incidentId || null,
        alertType: 'keyword_match',
        severity: this.calculateAlertSeverity(article, campaign),
        title: `Media Alert: ${article.title}`,
        summary: this.generateAlertSummary(article, campaign),
        article: article,
        isRead: false,
        isResolved: false,
        createdAt: serverTimestamp()
      });

      console.log(`🚨 Alert generated for: ${article.title}`);

    } catch (error) {
      console.error('❌ Error creating alert:', error);
    }
  }

  /**
   * Calculate alert severity based on content and rules
   */
  calculateAlertSeverity(article, campaign) {
    // Simple severity calculation - can be enhanced
    const title = article.title.toLowerCase();
    
    if (title.includes('emergency') || title.includes('crisis') || title.includes('urgent')) {
      return 'high';
    } else if (title.includes('incident') || title.includes('breaking')) {
      return 'medium';
    }
    
    return 'low';
  }

  /**
   * Generate alert summary
   */
  generateAlertSummary(article, campaign) {
    return `News article mentioning monitored keywords detected. Source: ${article.source}, Published: ${article.publishedAt}`;
  }

  /**
   * Generate unique article ID (safe for Unicode characters)
   */
  generateArticleId(article) {
    const url = article.url || '';
    const title = article.title || '';
    const source = article.source || '';
    
    // Create a simple hash from the content (safe for Unicode)
    const content = `${url}-${title}-${source}`;
    let hash = 0;
    
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Convert to positive number and base36 string
    const id = Math.abs(hash).toString(36);
    return `article_${id}_${Date.now()}`;
  }

  /**
   * Get articles for organization with filtering
   */
  async getArticlesForOrg(orgId, options = {}) {
    try {
      console.log(`🔍 DATABASE RETRIEVAL: Fetching articles for org=${orgId}, limit=${options.limit || 50}`);
      
      // 🔍 TEMPORARILY DISABLED FOR DEBUGGING: 30-day database filter
      console.log(`🔍 DATABASE FILTER: DISABLED for debugging - fetching ALL articles`);
      
      const articlesRef = collection(this.db, 'c3_media_articles');
      
      // 🔧 BACKWARDS COMPATIBLE: Get articles with OR without orgId for now
      // TODO: Remove this after data migration - should only filter by orgId
      let articleQuery = query(
        articlesRef,
        orderBy('relevanceScore', 'desc'),
        limit(options.limit || 50)
      );

      console.log(`📡 Executing Firestore query on c3_media_articles collection...`);
      const snapshot = await getDocs(articleQuery);
      
      const allArticles = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // 🔧 CLIENT-SIDE ORGID FILTERING (temporary - until data migration)
      console.log(`🔍 DEBUGGING FILTER: Looking for orgId="${orgId}"`);
      console.log(`🔍 SAMPLE ARTICLES WITH ORGIDS:`, allArticles.slice(0, 3).map(a => ({ 
        title: a.title?.substring(0, 30), 
        orgId: a.orgId, 
        relevanceScore: a.relevanceScore,
        publishedAt: a.publishedAt
      })));
      
      const articles = allArticles.filter(article => {
        const matches = !article.orgId || article.orgId === orgId || article.orgId === 'unknown';
        if (article.relevanceScore >= 0.95) {
          console.log(`🚨 HIGH RELEVANCE ARTICLE: "${article.title?.substring(0, 40)}" - orgId:"${article.orgId}" vs expected:"${orgId}" - Match:${matches} - Score:${article.relevanceScore}`);
        }
        return matches;
      });
      
      console.log(`✅ DATABASE RETRIEVAL SUCCESS: Found ${allArticles.length} total articles, ${articles.length} after org filtering`);
      
      // 🔍 DEBUG: Show relevance distribution of retrieved articles
      if (articles.length > 0) {
        console.log(`📰 Sample article: "${articles[0].title?.substring(0, 50)}..." (${articles[0].relevanceScore ? Math.round(articles[0].relevanceScore * 100) : 'no'}% relevance)`);
        
        // Show relevance distribution
        const relevanceDistribution = articles.map(a => Math.round((a.relevanceScore || 0) * 100)).sort((a, b) => b - a);
        console.log(`🎯 RELEVANCE DISTRIBUTION: [${relevanceDistribution.slice(0, 10).join('%, ')}%${relevanceDistribution.length > 10 ? ', ...' : ''}]`);
        
        // Specifically look for high relevance articles
        const highRelevanceCount = articles.filter(a => (a.relevanceScore || 0) >= 0.95).length;
        const highRelevanceArticles = articles.filter(a => (a.relevanceScore || 0) >= 0.95);
        console.log(`🚨 HIGH RELEVANCE (≥95%): Found ${highRelevanceCount} articles`);
        
        if (highRelevanceArticles.length > 0) {
          highRelevanceArticles.forEach((article, index) => {
            console.log(`   ${index + 1}. "${article.title?.substring(0, 50)}..." - ${Math.round((article.relevanceScore || 0) * 100)}% relevance`);
          });
        }
      } else {
        console.log(`⚠️ NO ARTICLES FOUND in c3_media_articles collection`);
      }
      
      return articles;

    } catch (error) {
      console.error('❌ Error fetching articles:', error);
      console.error('❌ Database retrieval failed - this could explain missing articles in UI');
      return [];
    }
  }

  /**
   * Get enhanced service metrics with filtering statistics
   */
  getMetrics() {
    const avgRelevanceScore = this.metrics.relevanceScores.length > 0 
      ? this.metrics.relevanceScores.reduce((a, b) => a + b, 0) / this.metrics.relevanceScores.length 
      : 0;

    // FORCE RUNNING STATUS FOR ACTIVE MONITORING
    console.log(`📊 Current isRunning status: ${this.isRunning}`);
    if (!this.isRunning) {
      console.log('🚨 Forcing monitoring to ACTIVE status');
      this.isRunning = true;
    }

    return {
      ...this.metrics,
      isRunning: this.isRunning, // Explicitly ensure this is true
      providersCount: this.feedProviders.length,
      processorsCount: this.articleProcessors.length,
      config: this.config,
      filteringStats: {
        totalProcessed: this.metrics.articlesProcessed,
        totalFiltered: this.metrics.articlesFiltered,
        filterRate: this.metrics.articlesProcessed > 0 
          ? (this.metrics.articlesFiltered / this.metrics.articlesProcessed * 100).toFixed(1) + '%'
          : '0%',
        avgRelevanceScore: avgRelevanceScore.toFixed(3)
      }
    };
  }

  /**
   * Update filtering configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    console.log('⚙️ Media Intelligence config updated:', newConfig);
    return this.config;
  }

  /**
   * Set filtering sensitivity preset
   */
  setFilteringSensitivity(level) {
    const presets = {
      strict: {
        minRelevanceScore: 0.75,
        filterSensitivity: 'strict',
        enableAdvancedFiltering: true
      },
      medium: {
        minRelevanceScore: 0.50,
        filterSensitivity: 'medium',
        enableAdvancedFiltering: true
      },
      relaxed: {
        minRelevanceScore: 0.30,
        filterSensitivity: 'relaxed',
        enableAdvancedFiltering: true
      }
    };

    console.log(`🎚️ SENSITIVITY CHANGE REQUEST: ${level}`);
    console.log(`🔧 Current config.filterSensitivity: ${this.config.filterSensitivity}`);

    if (presets[level]) {
      this.updateConfig(presets[level]);
      console.log(`✅ Filtering sensitivity updated to: ${level}`);
      console.log(`🎯 New config.filterSensitivity: ${this.config.filterSensitivity}`);
      console.log(`📊 New getRelevanceThreshold(): ${this.getRelevanceThreshold()}`);
      console.log(`💡 This means articles need ${Math.round(this.getRelevanceThreshold() * 100)}%+ relevance to pass`);
    } else {
      console.warn(`⚠️ Unknown sensitivity level: ${level}`);
    }

    return this.config;
  }

  /**
   * Get relevance threshold based on current sensitivity setting
   */
  getRelevanceThreshold() {
    const sensitivityThresholds = {
      strict: 0.75,   // 75%
      medium: 0.50,   // 50%
      relaxed: 0.30   // 30%
    };
    
    return sensitivityThresholds[this.config.filterSensitivity] || 0.50;
  }

  /**
   * Set up real-time listener for new articles
   */
  subscribeToArticles(orgId, callback) {
    try {
      // 🔍 TEMPORARILY DISABLED FOR DEBUGGING: 30-day real-time filter
      console.log(`🔍 REAL-TIME FILTER: DISABLED for debugging - subscribing to ALL articles`);
      
      const articlesRef = collection(this.db, 'c3_media_articles');
      
      // 🔧 BACKWARDS COMPATIBLE: Get articles with OR without orgId for now  
      // TODO: Remove this after data migration - should only filter by orgId
      const articleQuery = query(
        articlesRef,
        orderBy('relevanceScore', 'desc'),
        limit(20)
      );

      return onSnapshot(articleQuery, (snapshot) => {
        const allArticles = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // 🔧 CLIENT-SIDE ORGID FILTERING (temporary - until data migration)
        console.log(`🔍 REAL-TIME DEBUGGING FILTER: Looking for orgId="${orgId}"`);
        
        const articles = allArticles.filter(article => {
          const matches = !article.orgId || article.orgId === orgId || article.orgId === 'unknown';
          if (article.relevanceScore >= 0.95) {
            console.log(`🚨 REAL-TIME HIGH RELEVANCE: "${article.title?.substring(0, 40)}" - orgId:"${article.orgId}" vs expected:"${orgId}" - Match:${matches} - Score:${article.relevanceScore}`);
          }
          return matches;
        });
        
        console.log(`🔔 REAL-TIME UPDATE: Found ${allArticles.length} total articles, ${articles.length} after org filtering`);
        
        // 🔍 DEBUG: Show relevance distribution of real-time articles
        if (articles.length > 0) {
          const relevanceDistribution = articles.map(a => Math.round((a.relevanceScore || 0) * 100)).sort((a, b) => b - a);
          console.log(`🎯 REAL-TIME RELEVANCE: [${relevanceDistribution.slice(0, 10).join('%, ')}%${relevanceDistribution.length > 10 ? ', ...' : ''}]`);
          
          const highRelevanceCount = articles.filter(a => (a.relevanceScore || 0) >= 0.95).length;
          console.log(`🚨 REAL-TIME HIGH RELEVANCE (≥95%): Found ${highRelevanceCount} articles`);
        }
        
        callback(articles);
      });

    } catch (error) {
      console.error('❌ Error setting up articles subscription:', error);
      return () => {}; // Return empty function for cleanup
    }
  }

  /**
   * 💰 COST OPTIMIZATION: Check if there are any active incidents in the system
   * Returns true if active incidents exist, false otherwise
   */
  async checkForActiveIncidents() {
    try {
      if (!this.db) {
        console.warn('💰 COST OPTIMIZATION: No database connection - assuming active incidents exist');
        return true; // Conservative approach - if we can't check, assume incidents exist
      }

      // Check for any active incidents across all organizations
      const incidentsRef = collection(this.db, 'c3_incidents');
      const activeIncidentsQuery = query(
        incidentsRef,
        where('status', 'in', ['Active', 'Monitoring', 'In Progress']),
        limit(1) // We only need to know if any exist
      );

      const incidentSnapshot = await getDocs(activeIncidentsQuery);
      const hasActiveIncidents = !incidentSnapshot.empty;

      if (hasActiveIncidents) {
        console.log('💰 COST OPTIMIZATION: Active incidents detected - media intelligence processing justified');
      } else {
        console.log('💰 COST OPTIMIZATION: No active incidents found - media processing would be wasteful');
      }

      return hasActiveIncidents;

    } catch (error) {
      console.error('💰 COST OPTIMIZATION: Error checking for active incidents:', error);
      return true; // Conservative approach - if check fails, proceed with processing
    }
  }

  /**
   * Send system-generated chat message for high-relevance media articles
   */
  async sendSystemChatMessage(article) {
    try {
      // Get the organization ID from article or current context
      const orgId = article.orgId || this.currentOrgId;
      if (!orgId) {
        console.warn('⚠️ Cannot send system chat message - no orgId available');
        return;
      }

      // Find active incidents for this organization
      const incidentsRef = collection(this.db, 'c3_incidents');
      const activeIncidentsQuery = query(
        incidentsRef,
        where('orgId', '==', orgId),
        where('status', 'in', ['Active', 'Monitoring', 'In Progress']),
        orderBy('createdAt', 'desc'),
        limit(5) // Get recent active incidents
      );

      const incidentSnapshot = await getDocs(activeIncidentsQuery);
      
      if (incidentSnapshot.empty) {
        console.log('ℹ️ No active incidents found for system chat message');
        return;
      }

      // Create system message content
      const relevancePercent = Math.round(article.relevanceScore * 100);
      const systemMessage = `🚨 **HIGH-RELEVANCE MEDIA ALERT** (${relevancePercent}%)

**Article:** ${article.title}
**Source:** ${article.source}
**Published:** ${this.formatTimeAgo(article.publishedAt)}

${article.crisisIndicators?.length > 0 ? 
  `**Crisis Indicators:** ${article.crisisIndicators.map(ci => ci.type).join(', ')}` : 
  ''}

**Link:** ${article.url}

*This article was automatically detected by the Media Intelligence system due to its high relevance to your organization.*`;

      // Send to all active incidents
      for (const incidentDoc of incidentSnapshot.docs) {
        const incidentId = incidentDoc.id;
        
        try {
          await addDoc(collection(this.db, 'c3_incidentChats'), {
            incidentId: incidentId,
            message: systemMessage,
            timestamp: serverTimestamp(),
            authorId: 'system',
            authorName: 'Media Intelligence System',
            firstName: 'Media Intelligence',
            lastName: 'System',
            role: 'system',
            orgId: orgId,
            isSystemMessage: true,
            mediaArticleId: article.id,
            articleRelevanceScore: article.relevanceScore
          });

          // FORENSIC LOGGING: Log system chat message creation
          try {
            await createIncidentLog({
              incidentId: incidentId,
              orgId: orgId,
              action: ACTIVITY_TYPES.SYSTEM_CHAT_MESSAGE_SENT,
              details: `Media Intelligence system message sent for article: "${article.title}" (Relevance: ${relevancePercent}%)`,
              userId: 'system',
              userEmail: 'system@c3.com',
              userFullName: 'Media Intelligence System',
              priority: article.relevanceScore >= 0.98 ? 'high' : 'normal',
              metadata: {
                articleId: article.id,
                articleUrl: article.url,
                relevanceScore: article.relevanceScore,
                messageLength: systemMessage.length,
                incidentId: incidentId
              }
            });
          } catch (logError) {
            console.error('Error logging system chat message:', logError);
            // Don't fail message creation if logging fails
          }

          console.log(`📱 System chat message sent to incident ${incidentId} for article: ${article.title?.substring(0, 50)}...`);
          
        } catch (chatError) {
          console.error(`❌ Error sending system chat message to incident ${incidentId}:`, chatError);
        }
      }

    } catch (error) {
      console.error('❌ Error in sendSystemChatMessage:', error);
    }
  }

  /**
   * Format time ago for system messages
   */
  formatTimeAgo(date) {
    if (!date) return 'Unknown time';
    
    const now = new Date();
    const publishedDate = new Date(date);
    const diffMs = now - publishedDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    
    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  }

  /**
   * Set current organization ID for system messages
   */
  setCurrentOrgId(orgId) {
    this.currentOrgId = orgId;
  }

  /**
   * 🧠 GET GPT PERFORMANCE METRICS (PHASE 3)
   * 
   * Returns comprehensive performance metrics for GPT scoring system.
   * Used for monitoring, optimization, and comparison with keyword scoring.
   */
  getGPTMetrics() {
    if (!this.config.useGPTScoring || !this.metrics.gptMetrics) {
      return {
        enabled: false,
        message: 'GPT scoring is not enabled or not initialized'
      };
    }

    const baseMetrics = this.metrics.gptMetrics;
    const systemMetrics = this.getMetrics(); // Get overall system metrics
    
    return {
      enabled: true,
      initialization: {
        initialized: baseMetrics.initialized,
        initializationTime: baseMetrics.initializationTime,
        initializationError: baseMetrics.initializationError || null
      },
      performance: {
        processedArticles: baseMetrics.processedArticles || 0,
        averageConfidence: Math.round(baseMetrics.averageConfidence || 0),
        fallbackRate: Math.round((baseMetrics.fallbackRate || 0) * 100),
        averageTokenSavings: Math.round(baseMetrics.tokenSavings || 0),
        componentsLoaded: !!(this.smartTruncator && this.gptScorer)
      },
      comparison: {
        totalSystemArticles: systemMetrics.articlesProcessed,
        gptProcessedPercent: systemMetrics.articlesProcessed > 0 ? 
          Math.round((baseMetrics.processedArticles / systemMetrics.articlesProcessed) * 100) : 0,
        systemFilterRate: Math.round((systemMetrics.articlesFiltered / 
          (systemMetrics.articlesProcessed + systemMetrics.articlesFiltered)) * 100)
      },
      health: {
        status: this.getGPTHealthStatus(),
        recommendations: this.getGPTRecommendations()
      },
      lastUpdated: Date.now()
    };
  }

  /**
   * 🧠 GET GPT HEALTH STATUS
   */
  getGPTHealthStatus() {
    if (!this.config.useGPTScoring) return 'DISABLED';
    if (!this.metrics.gptMetrics) return 'NOT_INITIALIZED';
    if (this.metrics.gptMetrics.initializationError) return 'INITIALIZATION_FAILED';
    if (!this.metrics.gptMetrics.initialized) return 'INITIALIZING';
    
    const metrics = this.metrics.gptMetrics;
    const fallbackRate = metrics.fallbackRate || 0;
    const confidence = metrics.averageConfidence || 0;
    
    if (fallbackRate > 0.5) return 'HIGH_FALLBACK_RATE';
    if (confidence < 30) return 'LOW_CONFIDENCE';
    if (fallbackRate > 0.2) return 'MODERATE_ISSUES';
    if (confidence > 70 && fallbackRate < 0.1) return 'OPTIMAL';
    
    return 'HEALTHY';
  }

  /**
   * 🧠 GET GPT RECOMMENDATIONS
   */
  getGPTRecommendations() {
    const recommendations = [];
    
    if (!this.config.useGPTScoring) {
      recommendations.push('Enable GPT scoring to access AI-powered relevance assessment');
      return recommendations;
    }
    
    if (!this.metrics.gptMetrics || !this.metrics.gptMetrics.initialized) {
      recommendations.push('Initialize GPT components to begin processing');
      return recommendations;
    }
    
    const metrics = this.metrics.gptMetrics;
    const fallbackRate = metrics.fallbackRate || 0;
    const confidence = metrics.averageConfidence || 0;
    const tokenSavings = metrics.tokenSavings || 0;
    
    if (fallbackRate > 0.3) {
      recommendations.push('High fallback rate detected - consider increasing token limits');
    }
    
    if (confidence < 40) {
      recommendations.push('Low confidence scores - review content truncation settings');
    }
    
    if (tokenSavings < 20) {
      recommendations.push('Low token savings - consider more aggressive truncation');
    }
    
    if (tokenSavings > 80) {
      recommendations.push('Very high token savings - may be truncating too aggressively');
    }
    
    if (fallbackRate < 0.1 && confidence > 70) {
      recommendations.push('GPT scoring performing optimally');
    }
    
    return recommendations.length > 0 ? recommendations : ['System performing within normal parameters'];
  }

  /**
   * 🧠 GET MULTI-LAYER INTELLIGENCE METRICS
   * 
   * Returns comprehensive performance metrics for multi-layer intelligence system.
   * Used for monitoring enhancement effectiveness and system optimization.
   */
  getIntelligenceMetrics() {
    if (!this.config.useMultiLayerIntelligence || !this.metrics.intelligenceMetrics) {
      return {
        enabled: false,
        message: 'Multi-layer intelligence is not enabled or not initialized'
      };
    }

    const baseMetrics = this.metrics.intelligenceMetrics;
    const systemMetrics = this.getMetrics(); // Get overall system metrics
    
    return {
      enabled: true,
      initialization: {
        initialized: baseMetrics.enabled,
        layersActive: baseMetrics.layersActive || 0,
        initializationError: baseMetrics.initializationError || null
      },
      performance: {
        enhancementsApplied: baseMetrics.enhancementsApplied || 0,
        averageEnhancement: Math.round(baseMetrics.averageEnhancement || 0),
        falsePositivesReduced: baseMetrics.falsePositivesReduced || 0,
        falseNegativesCaught: baseMetrics.falseNegativesCaught || 0,
        componentsLoaded: !!this.multiLayerIntelligence
      },
      comparison: {
        totalSystemArticles: systemMetrics.articlesProcessed,
        enhancedPercent: systemMetrics.articlesProcessed > 0 ? 
          Math.round((baseMetrics.enhancementsApplied / systemMetrics.articlesProcessed) * 100) : 0,
        systemFilterRate: Math.round((systemMetrics.articlesFiltered / 
          (systemMetrics.articlesProcessed + systemMetrics.articlesFiltered)) * 100)
      },
      health: {
        status: this.getIntelligenceHealthStatus(),
        recommendations: this.getIntelligenceRecommendations()
      },
      lastUpdated: Date.now()
    };
  }

  /**
   * 🧠 GET INTELLIGENCE HEALTH STATUS
   */
  getIntelligenceHealthStatus() {
    if (!this.config.useMultiLayerIntelligence) return 'DISABLED';
    if (!this.metrics.intelligenceMetrics) return 'NOT_INITIALIZED';
    if (this.metrics.intelligenceMetrics.initializationError) return 'INITIALIZATION_FAILED';
    if (!this.metrics.intelligenceMetrics.enabled) return 'INITIALIZING';
    
    const metrics = this.metrics.intelligenceMetrics;
    const enhancementsApplied = metrics.enhancementsApplied || 0;
    const layersActive = metrics.layersActive || 0;
    
    if (layersActive < 5) return 'PARTIAL_FUNCTIONALITY';
    if (enhancementsApplied === 0) return 'NO_ACTIVITY';
    if (layersActive === 7 && enhancementsApplied > 10) return 'OPTIMAL';
    
    return 'HEALTHY';
  }

  /**
   * 🧠 GET INTELLIGENCE RECOMMENDATIONS
   */
  getIntelligenceRecommendations() {
    const recommendations = [];
    
    if (!this.config.useMultiLayerIntelligence) {
      recommendations.push('Enable multi-layer intelligence to dramatically improve relevance accuracy');
      return recommendations;
    }
    
    if (!this.metrics.intelligenceMetrics || !this.metrics.intelligenceMetrics.enabled) {
      recommendations.push('Initialize multi-layer intelligence components to begin enhanced processing');
      return recommendations;
    }
    
    const metrics = this.metrics.intelligenceMetrics;
    const enhancementsApplied = metrics.enhancementsApplied || 0;
    const layersActive = metrics.layersActive || 0;
    const averageEnhancement = metrics.averageEnhancement || 0;
    
    if (layersActive < 7) {
      recommendations.push(`Only ${layersActive}/7 intelligence layers active - check configuration`);
    }
    
    if (enhancementsApplied === 0) {
      recommendations.push('No enhancements applied yet - may need article processing to begin');
    }
    
    if (averageEnhancement < -10) {
      recommendations.push('Large negative average enhancement - review scoring weights');
    }
    
    if (averageEnhancement > 50) {
      recommendations.push('Very high average enhancement - may be over-boosting scores');
    }
    
    if (layersActive === 7 && enhancementsApplied > 10 && Math.abs(averageEnhancement) < 20) {
      recommendations.push('Multi-layer intelligence performing optimally');
    }
    
    return recommendations.length > 0 ? recommendations : ['System performing within normal parameters'];
  }

  /**
   * 🧠 GET COMBINED SCORING METRICS
   * 
   * Returns comprehensive metrics covering all scoring systems
   */
  getCombinedScoringMetrics() {
    const gptMetrics = this.getGPTMetrics();
    const intelligenceMetrics = this.getIntelligenceMetrics();
    const systemMetrics = this.getMetrics();
    
    return {
      system: systemMetrics,
      gpt: gptMetrics,
      intelligence: intelligenceMetrics,
      overview: {
        gptEnabled: gptMetrics.enabled,
        intelligenceEnabled: intelligenceMetrics.enabled,
        scoringMode: this.config.useGPTScoring ? 'GPT + Intelligence' : 
                     this.config.useMultiLayerIntelligence ? 'Keyword + Intelligence' : 'Basic Keyword',
        overallHealth: this.getCombinedHealthStatus(gptMetrics, intelligenceMetrics)
      }
    };
  }

  /**
   * Get combined health status across all scoring systems
   */
  getCombinedHealthStatus(gptMetrics, intelligenceMetrics) {
    const statuses = [];
    
    if (gptMetrics.enabled) {
      statuses.push(gptMetrics.health?.status || 'UNKNOWN');
    }
    
    if (intelligenceMetrics.enabled) {
      statuses.push(intelligenceMetrics.health?.status || 'UNKNOWN');
    }
    
    if (statuses.length === 0) return 'BASIC_KEYWORD_ONLY';
    
    // Return worst status for conservative health reporting
    const priorityOrder = ['INITIALIZATION_FAILED', 'HIGH_FALLBACK_RATE', 'PARTIAL_FUNCTIONALITY', 'MODERATE_ISSUES', 'HEALTHY', 'OPTIMAL'];
    
    for (const priority of priorityOrder) {
      if (statuses.includes(priority)) {
        return priority;
      }
    }
    
    return 'HEALTHY';
  }
}

export default MediaIntelligenceService; 